import React, { useState, useRef  } from 'react';
import { Input, Dropdown, Option, Field, Button, Spinner } from '@fluentui/react-components';
import { useResponderTeam, useUpdateResponderTeam } from '#root/queries/responders';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteDropdown } from '#root/hooks/useInfiniteDropdown';
import { useInfiniteLocations, useLocationById, Location } from '#root/queries/locations';
import { RESPONDER_STATUS_FORM, RESPONDER_ROLES_CONST, USR_TEAMS } from '#root/utils/responders';
import InfiniteDropdown from '#root/components/InfiniteDropdown';
import { Breadcrumbs } from '#root/components/Breadcrumbs';
import ButtonGroup from '#root/components/ButtonGroup';
import { getEnumLabelByValue } from '#root/utils/enums';
import { showToast } from '#root/components/GlobalToaster';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from "#root/routes/constants";
import './styles.scss';

interface ResponderTeam {
    incidentName?: string;
    firstName?: string;
    lastName?: string;
    nickname?: string;
    departmentOrAgency?: string;
    role?: number;
    usarTeamNumber?: number;
    status?: number;
    //isIndividualAssignment?: boolean;
    locationId?: string;
    group?: string | number;
}

interface ResponderTeamFormInnerProps {
    data: { item: ResponderTeam };
    updateResponderTeam: ReturnType<typeof useUpdateResponderTeam>;
    incidentId: string;
    teamNumber: number;
}

function getInitialValues(item: ResponderTeam | undefined) {
    return {
        locationId: item?.locationId ?? undefined,
        status: item?.status,
        group: item?.group !== undefined ? String(item.group) : undefined,
        //isIndividualAssignment: item?.isIndividualAssignment ?? false,
    };
}

const ResponderTeamFormInner: React.FC<ResponderTeamFormInnerProps> = ({
    data,
    updateResponderTeam,
    incidentId,
    teamNumber,
}) => {
    const initial = getInitialValues(data?.item);
    const [locationId, setLocationId] = useState<string | undefined>(initial.locationId);
    const [status, setStatus] = useState<number | undefined>(initial.status);
    const [group, setGroup] = useState<string | undefined>(initial.group);
    //const [isIndividualAssignment, setIsIndividualAssignment] = useState<boolean>(initial.isIndividualAssignment);
    const navigate = useNavigate();
    const {
        selectedData: selectedLocationData,
        handleChange: handleLocationChange,
        setFilter: setLocationFilter,
        queryResult: locationsQuery,
    } = useInfiniteDropdown<Location>({
        initialId: locationId,
        useInfiniteQuery: useInfiniteLocations,
        useItemById: useLocationById,
        queryParameters: {
            incidentId,
            includeOrganizationLocations: true,
        }
    });

    const handleSave = async () => {
        if (!status || !locationId) return;

        try {
            await updateResponderTeam.mutateAsync({
                request: {
                    incidentId,
                    usarTeamNumber: teamNumber,
                    status,
                    locationId
                }
            });
            navigate(ROUTES.MERRGE211.CHECKED_IN_DEPLOYABLE);
        } catch (error) {
            showToast("Failed to update team", "error");
            //navigate(ROUTES.MERRGE211.CHECKED_IN_DEPLOYABLE);
        }
    };


    const r = data.item;

    return (
        <div className="emc">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs currentPageName="Review Team's Details" />
                    </div>
                </div>
            </div>
            <div className="team-leader-form">
                <Field label="Incident">
                    <Input value={r.incidentName ?? ''} readOnly />
                </Field>
                <h3>
                    Team Leader <div className="divider"></div>
                </h3>
                <div className="field-two-columns">
                    <Field label="First Name">
                        <Input value={r.firstName ?? ''} readOnly />
                    </Field>
                    <Field label="Last Name">
                        <Input value={r.lastName ?? ''} readOnly />
                    </Field>
                </div>
                <Field label="Nickname">
                    <Input value={r.nickname ?? ''} readOnly />
                </Field>
                <Field label="Department/Agency">
                    <Input value={r.departmentOrAgency ?? ''} readOnly />
                </Field>
                {r.role !== undefined && r.role !== null && (
                    <Field label="Role">
                        <ButtonGroup
                            name="role"
                            options={
                                RESPONDER_ROLES_CONST.filter(roleItem => roleItem.value === r.role).map(roleItem => ({
                                    label: roleItem.label,
                                    value: roleItem.value,
                                }))
                            }
                            value={r.role}
                            onChange={() => { }}
                        />
                    </Field>
                )}
                <Field label="US&R Team">
                    <Dropdown value={getEnumLabelByValue(USR_TEAMS, r.usarTeamNumber) || ''} disabled>
                        <Option value={r.usarTeamNumber?.toString() || ''}>
                            {getEnumLabelByValue(USR_TEAMS, r.usarTeamNumber) || ''}
                        </Option>
                    </Dropdown>
                </Field>
                <h3>
                    Review/Assign Location & Status <div className="divider"></div>
                </h3>
                <div className="form-row">
                    <Field label="Assigned Location" required className="span-2">
                        <InfiniteDropdown
                            value={locationId}
                            onChange={(_, data) => {
                                handleLocationChange(data.value);
                                setLocationId(data.value);
                            }}
                            onFilterChange={setLocationFilter}
                            queryResult={locationsQuery}
                            itemToOption={location => ({
                                value: location.id ?? '',
                                text: location.name ?? '',
                            })}
                            placeholder="Select location"
                            selectedItem={selectedLocationData}
                            required
                        />
                    </Field>
                    <Field label="Group" required>
                        <Dropdown value={"Not Applicable"} onOptionSelect={(_, data) => setGroup(String(data.optionValue))}>
                            <Option key={0} value={"0"}>
                                Not Applicable
                            </Option>
                        </Dropdown>
                    </Field>
                    <Field label="Status" required>
                        <Dropdown
                            value={status !== undefined ? getEnumLabelByValue(RESPONDER_STATUS_FORM, status) : ''}
                            onOptionSelect={(_, data) => setStatus(Number(data.optionValue))}
                        >
                            {RESPONDER_STATUS_FORM.map(statusOpt => (
                                <Option key={statusOpt.value} value={String(statusOpt.value)}>
                                    {statusOpt.label}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>
                    {/*                    <Field label="Individual Assignment" required>
                        <ButtonGroup
                            options={[
                                { value: true, label: 'Yes' },
                                { value: false, label: 'No' },
                            ]}
                            onChange={value => setIsIndividualAssignment(value)}
                            name="isIndividualAssignment"
                            value={isIndividualAssignment}
                        />
                    </Field>*/}
                </div>
                <div className="form-actions">
                    <Button appearance="primary" onClick={handleSave} disabled={updateResponderTeam.isPending}>
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};

const ResponderTeamForm: React.FC = () => {
    const [searchParams] = useSearchParams();
    const incidentId = searchParams.get('incidentId') || '';
    const teamNumber = Number(searchParams.get('teamNumber'));
    const { data, isLoading, error } = useResponderTeam({ incidentId, teamNumber });
    const updateResponderTeam = useUpdateResponderTeam();
    const navigate = useNavigate();
    const key = incidentId + '-' + teamNumber + '-' + (teamNumber ? 'data' : '');
    const errorToastShown = useRef(false);

    if (!incidentId || !teamNumber || isNaN(teamNumber)) {
        return <div>Incident ID and Team Number are required</div>;
    }

    if (error && !errorToastShown.current) {
        errorToastShown.current = true;
        showToast("Failed to load team data", "error");
        navigate(ROUTES.MERRGE211.CHECKED_IN_DEPLOYABLE);
        return null;
    }

    if (isLoading || !data?.item) {
        return <Spinner />;
    }

    return (
        <ResponderTeamFormInner
            key={key}
            data={data}
            updateResponderTeam={updateResponderTeam}
            incidentId={incidentId}
            teamNumber={teamNumber}
        />
    );
};

export default ResponderTeamForm;
